import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, ArrowRight, Users, GraduationCap, Wrench } from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import type { GrowthEnginesData } from '../../services/api';

interface GrowthEnginesProps {
  data: GrowthEnginesData;
}

const fmtMoney = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

const monthLabel = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleString('en', { month: 'short' });

const DeltaPill: React.FC<{ pct: number | null; suffix?: string }> = ({ pct, suffix = 'MoM' }) => {
  if (pct === null) return <span className="text-xs text-gray-400">no comparison</span>;
  const up = pct > 0;
  const flat = pct === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
      flat ? 'bg-gray-100 text-gray-500' : up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
    }`}>
      {up ? <ArrowUpRight size={12} /> : flat ? null : <ArrowDownRight size={12} />}
      {Math.abs(pct).toFixed(1)}% {suffix}
    </span>
  );
};

interface EngineCardProps {
  icon: React.ReactNode;
  iconClass: string;
  title: string;
  linkTo: string;
  headline: string;
  headlineNote: string;
  pill: React.ReactNode;
  chart: React.ReactNode;
  stats: Array<{ label: string; value: string }>;
}

const EngineCard: React.FC<EngineCardProps> = ({
  icon, iconClass, title, linkTo, headline, headlineNote, pill, chart, stats,
}) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className={`rounded-lg p-2 ${iconClass}`}>{icon}</span>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <Link
        to={linkTo}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
      >
        Report <ArrowRight size={12} />
      </Link>
    </div>
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-900 tabular-nums">{headline}</span>
        {pill}
      </div>
      <div className="text-xs text-gray-400 mt-0.5">{headlineNote}</div>
    </div>
    <div className="h-16 -mx-1">{chart}</div>
    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
      {stats.map(s => (
        <div key={s.label}>
          <div className="text-sm font-semibold text-gray-900 tabular-nums">{s.value}</div>
          <div className="text-[11px] text-gray-400 leading-tight">{s.label}</div>
        </div>
      ))}
    </div>
  </div>
);

const tooltipStyle = { fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' } as const;

export const GrowthEngines: React.FC<GrowthEnginesProps> = ({ data }) => {
  const { membership, postSchool } = data;

  const memberChart = membership.months.map(m => ({
    label: monthLabel(m.month),
    recurring: m.recurring,
    programs: m.l4 + m.l5,
  }));

  const eventChart = postSchool.events.map(e => ({
    label: new Date(e.ended + 'T00:00:00').toLocaleString('en', { month: 'short', day: 'numeric' }),
    post60: e.post60d,
    clubs: e.clubs60d,
  }));

  const latestMonthName = membership.latestMonth
    ? new Date(membership.latestMonth + 'T00:00:00').toLocaleString('en', { month: 'long' })
    : '';
  const latestPrograms = membership.months.length > 0
    ? (() => {
        const m = membership.months.find(x => x.month === membership.latestMonth);
        return m ? m.l4 + m.l5 : 0;
      })()
    : 0;

  const { totals } = postSchool;
  const avgPerAttendee = totals.attendees > 0 ? totals.post60 / totals.attendees : 0;
  const avgPerClubBuyer = totals.clubBuyers60 > 0 ? totals.clubs60 / totals.clubBuyers60 : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <EngineCard
        icon={<Users size={16} />}
        iconClass="bg-violet-50 text-violet-600"
        title="Membership"
        linkTo="/financials/membership"
        headline={fmtMoney(membership.latestRecurring)}
        headlineNote={`Recurring collected in ${latestMonthName}`}
        pill={<DeltaPill pct={membership.momPct} />}
        chart={
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={memberChart} barCategoryGap="25%">
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval={0} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: number, name: string) => [fmtMoney(v), name === 'recurring' ? 'Recurring' : 'L4+L5 programs']}
              />
              <Bar dataKey="recurring" stackId="m" fill="#8b5cf6" radius={[0, 0, 0, 0]} />
              <Bar dataKey="programs" stackId="m" fill="#ddd6fe" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        }
        stats={[
          { label: 'Active subs', value: membership.subs.active.toLocaleString() },
          { label: 'In trial', value: membership.subs.inTrial.toLocaleString() },
          { label: `L4+L5 (${latestMonthName.slice(0, 3)})`, value: fmtMoney(latestPrograms) },
        ]}
      />

      <EngineCard
        icon={<GraduationCap size={16} />}
        iconClass="bg-amber-50 text-amber-600"
        title="Schools → Post-School"
        linkTo="/financials/post-school"
        headline={fmtMoney(totals.post60)}
        headlineNote={`60-day follow-on revenue · ${totals.events} schools, last ~5 months`}
        pill={
          postSchool.attachMultiple !== null ? (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700">
              {postSchool.attachMultiple.toFixed(2)}× ticket rev
            </span>
          ) : (
            <span className="text-xs text-gray-400">windows still open</span>
          )
        }
        chart={
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={eventChart} barCategoryGap="25%">
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: number) => [fmtMoney(v), 'Post-60d revenue']}
              />
              <Bar dataKey="post60" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        }
        stats={[
          { label: 'Attendees', value: totals.attendees.toLocaleString() },
          { label: 'Ticket revenue', value: fmtMoney(totals.ticketRev) },
          { label: 'Avg / attendee', value: fmtMoney(avgPerAttendee) },
        ]}
      />

      <EngineCard
        icon={<Wrench size={16} />}
        iconClass="bg-blue-50 text-blue-600"
        title="Club Attach"
        linkTo="/financials/business-lines"
        headline={fmtMoney(totals.clubs60)}
        headlineNote="Custom clubs bought within 60 days of a school"
        pill={
          postSchool.clubAttachRate !== null ? (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-blue-50 text-blue-700">
              {postSchool.clubAttachRate.toFixed(1)}% of attendees
            </span>
          ) : (
            <span className="text-xs text-gray-400">windows still open</span>
          )
        }
        chart={
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={eventChart} barCategoryGap="25%">
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: number) => [fmtMoney(v), 'Club revenue (60d)']}
              />
              <Bar dataKey="clubs" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        }
        stats={[
          { label: 'Club buyers', value: totals.clubBuyers60.toLocaleString() },
          { label: 'Avg / buyer', value: fmtMoney(avgPerClubBuyer) },
          { label: 'Share of post-60d', value: totals.post60 > 0 ? `${Math.round((totals.clubs60 / totals.post60) * 100)}%` : '--' },
        ]}
      />
    </div>
  );
};
